import { useEffect, useState } from 'react'
import { money } from '../../domain/format'
import { browserTimeZone, pickRegion, resolvedBox } from '../../domain/plans'
import type { Plans } from '../../domain/schemas'
import { ChoiceCard } from './ui'

/**
 * The picked region, preselected from the browser's time zone once plans arrive. Re-picks if the chosen region
 * stops being available on a refetch.
 */
export function usePlanRegion(plans: Plans | null, timeZone: string | undefined = browserTimeZone()) {
  const [value, setValue] = useState<string | null>(null)
  const [suggested, setSuggested] = useState<string | null>(null)
  useEffect(() => {
    if (!plans) return
    const pick = pickRegion(plans, timeZone)
    setSuggested(pick.suggested)
    setValue((cur) => (cur && plans.regions.some((r) => r.key === cur && r.available) ? cur : pick.value))
  }, [plans, timeZone])
  return { value, setValue, suggested }
}

/**
 * One plan, three regions. Founder-facing words only: no vendor, region slug or size slug, except in `admin`, which
 * adds each region's resolved size and placement in small mono.
 */
export function PlanPicker({
  plans,
  value,
  suggested,
  onChange,
  admin = false,
}: {
  plans: Plans
  value: string | null
  suggested: string | null
  onChange: (key: string) => void
  admin?: boolean
}) {
  const { plan, regions } = plans
  const picked = resolvedBox(plans, value)
  const none = !regions.some((r) => r.available)
  return (
    <div className="plan-picker">
      <div className="plan">
        <div className="plan-name">{plan.name}</div>
        <div className="plan-specs">
          {plan.cores} processor cores · {plan.memoryGb} GB memory · {plan.diskGb} GB storage
        </div>
        <p className="plan-summary">{plan.summary}</p>
        <div className="price-line">
          <b className="mono">{picked ? money(picked.priceMonthly) : '–'}</b>
          <span>Listen covers this during the pilot</span>
        </div>
      </div>

      <div className="plan-regions">
        <div className="field-label">Region</div>
        <span className="hint">Pick the one closest to you.</span>
        {none && <p className="empty">No region has room right now. Try again in a few minutes.</p>}
        <div className="choices choices-3" role="radiogroup" aria-label="Region">
          {regions.map((r) => (
            <ChoiceCard key={r.key} name="plan-region" value={r.key} checked={value === r.key} off={!r.available} onPick={onChange}>
              <span className="choice-top">
                <b>{r.label}</b>
                {r.available && r.priceMonthly !== null && <span className="mono">{money(r.priceMonthly)}</span>}
              </span>
              <span className="muted">{r.city}</span>
              {admin && r.available && (
                <span className="mono muted choice-admin">
                  {r.size} · {r.region} · {r.placement}
                </span>
              )}
              {!r.available && <span className="label">Not available right now</span>}
              {r.key === suggested && r.available && <span className="suggest">Suggested: closest to you</span>}
            </ChoiceCard>
          ))}
        </div>
      </div>
    </div>
  )
}
